import { createTheme, type Theme } from '@mui/material/styles';
import type { BrandFont, BrandRoles, BrandTheme } from './brandTheme';

/**
 * The ONLY source of visual tokens (DS-005, TYPE-012), built at runtime from the tenant's brand
 * document (CNPWKS-0007). Both colour schemes come from the brand; the person picks which one
 * applies in Preferences (CNPWKS-0006). Roles are used as served: no colour is derived here.
 */

type Status = 'error' | 'warning' | 'info' | 'success';

function status(r: BrandRoles, s: Status) {
  return {
    main: r[`${s}Fill`],
    contrastText: r[`${s}Ink`],
    light: r[`${s}Soft`],
    dark: r[`${s}Text`],
  };
}

function scheme(r: BrandRoles) {
  return {
    palette: {
      primary: { main: r.brand, dark: r.brandHover, light: r.brandSoft, contrastText: r.brandInk },
      secondary: {
        main: r.accent,
        dark: r.accentHover,
        light: r.accentSoft,
        contrastText: r.accentInk,
      },
      error: status(r, 'error'),
      warning: status(r, 'warning'),
      info: status(r, 'info'),
      success: status(r, 'success'),
      background: { default: r.bg, paper: r.surface },
      text: { primary: r.text, secondary: r.text2, disabled: r.text3 },
      divider: r.line,
      action: {
        active: r.text2,
        hover: r.hover,
        selected: r.selected,
        focus: r.hover,
        disabled: r.text3,
        disabledBackground: r.surface2,
      },
      AppBar: { defaultBg: r.appBar },
      Avatar: { defaultBg: r.brand },
      brandChrome: {
        appBar: r.appBar,
        appBarInk: r.appBarInk,
        appBarAccent: r.appBarAccent,
        boundary: r.brandBoundary,
        link: r.link,
        focusRing: r.focusRing,
        lineStrong: r.lineStrong,
        scrim: r.scrim,
        avatar: r.brand,
        avatarInk: r.brandInk,
      },
    },
  };
}

/** C10: the largest listed weight ≤ 600, or 600 when the face lists none. */
export function emphasisWeight(font: BrandFont): number {
  const usable = font.weights.filter((w) => w <= 600);
  return usable.length > 0 ? Math.max(...usable) : 600;
}

export function buildAppTheme(brand: BrandTheme): Theme {
  const heading = brand.fonts.heading.stack;
  const headingWeight = emphasisWeight(brand.fonts.heading);
  const bodyWeight = emphasisWeight(brand.fonts.body);

  return createTheme({
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: { light: scheme(brand.modes.light), dark: scheme(brand.modes.dark) },
    shape: { borderRadius: brand.radius.control },
    typography: {
      fontFamily: brand.fonts.body.stack,
      fontWeightRegular: 400,
      fontWeightMedium: bodyWeight,
      h1: {
        fontFamily: heading,
        fontSize: '1.75rem',
        lineHeight: 1.2857,
        fontWeight: headingWeight,
      }, // 28/36 page heading
      h2: {
        fontFamily: heading,
        fontSize: '1.125rem',
        lineHeight: 1.3333,
        fontWeight: headingWeight,
      }, // 18/24 section heading
      h3: { fontFamily: heading },
      h4: { fontFamily: heading },
      h5: { fontFamily: heading },
      h6: { fontFamily: heading },
      body1: { fontSize: '1rem', lineHeight: 1.5 }, // 16/24 reading text
      body2: { fontSize: '0.875rem', lineHeight: 1.4286 }, // 14/20 working text
      caption: { fontSize: '0.75rem', lineHeight: 1.3333 }, // 12/16 metadata
      button: { fontSize: '0.875rem', fontWeight: bodyWeight, textTransform: 'none' },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          // Repeated under the dark selector: MUI's own dark AppBar rule is more specific.
          colorDefault: ({ theme }) => ({
            backgroundColor: theme.vars.palette.brandChrome.appBar,
            color: theme.vars.palette.brandChrome.appBarInk,
            ...theme.applyStyles('dark', {
              backgroundColor: theme.vars.palette.brandChrome.appBar,
              color: theme.vars.palette.brandChrome.appBarInk,
            }),
          }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: ({ theme }) => ({ color: theme.vars.palette.brandChrome.avatarInk }),
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&:not(.MuiBackdrop-invisible)': {
              backgroundColor: theme.vars.palette.brandChrome.scrim,
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.vars.palette.brandChrome.link }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: ({ theme }) => ({
            borderColor: theme.vars.palette.brandChrome.lineStrong,
          }),
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.vars.palette.brandChrome.focusRing}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      // CNPWKS-0011 P2: MUI paints selection from primary.main; use the brand's served layers instead,
      // plus a leading bar so selection never depends on colour alone (NAV-001).
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
              backgroundColor: theme.vars.palette.action.selected,
              boxShadow: `inset ${theme.spacing(0.5)} 0 0 ${theme.vars.palette.primary.main}`,
            },
            '&.Mui-selected:hover': {
              backgroundColor: theme.vars.palette.action.selected,
              backgroundImage: `linear-gradient(${theme.vars.palette.action.hover}, ${theme.vars.palette.action.hover})`,
            },
          }),
        },
      },
      MuiCard: { styleOverrides: { root: { borderRadius: brand.radius.card } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: brand.radius.dialog } } },
    },
  });
}
